import mainActions from '@/assets/policies/actions.json' with { type: 'json' }
import alphaActions from '@/assets/policies/alpha-actions.json' with { type: 'json' }
import { resolveRoute } from '@/router/resolve'
import { useAuthStore } from '@/store/pinia/auth'
import { useDevStore } from '@/store/pinia/dev'
import { useRootStore } from '@/store/pinia/root'
import { appMode } from '../config/mode'

const Actions = { ...alphaActions, ...mainActions }

const checkUserPermissionsError = '[checkUserPermissions] Passed invalid action, please correct it'
const canDisplayError = `[checkUserPermissions] was passed an Array.

Arrays are invalid. Instead pass params normally:

e.g. checkUserPermissions('UpdateItem')
e.g. checkUserPermissions('UpdateItem', item.id)
`

/** Cycloid permissions checker
 *
 * @typedef {import('vue-router').Location} RouteLocation
 * @typedef {import('vue-router').Router} VueRouter
 */

/** Adds key property with name value to each action.
 * It is used to be able to target specific action by name.
 *
 * @returns {keyof Actions} Actions
 */
export function mapActionNameToKeyPerAction () {
  const actions = {
    ...Actions,
  }

  for (const key of _.keys(actions)) actions[key].key = key

  return actions
}

export const systemDefinedActions = mapActionNameToKeyPerAction()

/** Checks whether the user can perform the given actions.
 *
 * This function calls canDo() to allow or forbid the user triggered action.
 * If action is triggered on specific resource it scopes evaluation to a specified resource only.
 * It returns the boolean value depending of the outcome of evaluation call.
 * Returns false if something goes wrong and policyAction.
 * @param {keyof Actions}  action        - The permission key. (e.g. 'GetOrgs')
 * @param {string} [identifier]    - An entity id. (e.g. canonical, id, ref, etc.)
 *
 *
 * @example
 * checkUserPermissions('GetOrgs')
 * checkUserPermissions('GetOrg', 'cycloid')
 *
 * @returns {Boolean}                   Whether user can access that entity/endpoint
 */
export function checkUserPermissions (action, identifier) {
  const authStore = useAuthStore()
  const rootStore = useRootStore()

  const invalidActions = getInvalidActions(action)
  if (invalidActions) console.error(checkUserPermissionsError, invalidActions)

  if (rootStore.isOrgAdmin) return true
  if (!authStore.evaluatedActions?.[action]) return false

  const { ok = false, entityCanonicals = [] } = authStore.evaluatedActions?.[action] ?? {}

  return _.some([identifier, entityCanonicals], _.isEmpty)
    ? ok
    : checkEntityCanonicalMatch(entityCanonicals, identifier)
}

export function checkEntityCanonicalMatch (entityCanonicals, identifier) {
  return _.some(entityCanonicals, (entityCanonical) => {
    return new RegExp(`^${entityCanonical.replaceAll('*', '.*')}$`).test(identifier)
  })
}

// Component write actions the backend also grants to the project's owner, permission or not
// (BE-473 owner fallback). The console mirrors that so a Project Creator can manage the
// components of the projects it owns without holding the component permissions.
const projectOwnerFallbackActions = ['CreateComponent', 'UpdateComponent', 'DeleteComponent']

/**
 * Whether the current user owns the given project.
 *
 * `owns` is the list of owned project resources the backend authorizes its owner fallback
 * against, delivered with the session alongside `permissions`, so the console checks membership
 * of the same list rather than re-deriving ownership.
 *
 * @param {string} orgCanonical
 * @param {string} projectCanonical
 * @returns {boolean}
 */
export function ownsProject (orgCanonical, projectCanonical) {
  if (_.some([orgCanonical, projectCanonical], _.isEmpty)) return false
  const authStore = useAuthStore()
  return _.includes(authStore.owns, `organization:${orgCanonical}:project:${projectCanonical}`)
}

/**
 * Whether project ownership alone satisfies the action, mirroring the backend owner fallback for
 * component writes. Lets an owner reach a component route its component permissions would gate.
 *
 * @param {keyof Actions} action
 * @param {{ orgCanonical?: string, projectCanonical?: string }} [canonicals]
 * @returns {boolean}
 */
export function isSatisfiedByProjectOwnership (action, { orgCanonical, projectCanonical } = {}) {
  if (!_.includes(projectOwnerFallbackActions, action)) return false
  return ownsProject(orgCanonical, projectCanonical)
}

/**
 * Whether an action has been evaluated and explicitly denied.
 *
 * Unlike `!canDisplay(action)`, this is false for never-evaluated actions, so display-only
 * gates (e.g. nav sections, whose pages stay protected by route checks) can fail open
 * instead of hiding before the action has been evaluated. Org-admins are never denied.
 *
 * Standalone so the router guards can reuse it without the router-bound checker object.
 *
 * @param {keyof Actions} action - The permission key. (e.g. 'GetMenuDashboard')
 * @returns {boolean}
 */
export function isActionDenied (action) {
  const rootStore = useRootStore()
  if (rootStore.isOrgAdmin) return false
  const evaluated = useAuthStore().evaluatedActions?.[action]
  return Boolean(evaluated) && !evaluated.ok
}

/**
 * Whether an action already has an evaluation result. Lets a gate distinguish "unknown,
 * still loading" (hide to avoid a flash) from "known" (a re-evaluation shouldn't blink it).
 * Org-admins never evaluate actions, so they count as known.
 *
 * @param {keyof Actions} action - The permission key. (e.g. 'GetMenuDashboard')
 * @returns {boolean}
 */
export function isActionEvaluated (action) {
  if (useRootStore().isOrgAdmin) return true
  return Boolean(useAuthStore().evaluatedActions?.[action])
}

/**
 * Get required actions for a given route
 *
 * @param {VueRouter} router        - Vue router instance
 * @param {RouteLocation} routeLocation - Route location
 *
 * @example                      - getRouteRequiredActions(router, { name: 'events' })
 * @returns {keyof Actions[]}              - The actions that a user must have to visit the given route
 */
export function getRouteRequiredActions (router, routeLocation) {
  if (!router) return []
  const route = resolveRoute(routeLocation, null, { router })
  const requiredActions = _.get(route, 'meta.requiredActions', [])
  const computedActions = (_.isFunction(requiredActions))
    ? requiredActions()
    : requiredActions
  return _.without(computedActions, ..._.keys(alphaActions))
}

/**
 * Check the users permissions against the given route
 *
 * @param {VueRouter} router        - Vue router instance
 * @param {RouteLocation} routeLocation - Route location
 *
 * @example                      - checkUserRoutePermissions(router, { name: 'events' })
 * @returns {boolean}            - Does the user have sufficient permissions to access the given route?
 */
export function checkUserRoutePermissions (router, routeLocation) {
  if (!router) return false
  if (_.isString(routeLocation)) console.warn(`[checkUserRoutePermissions] routeLocation should be an object in the { name: 'routeName' } format. (passed '${routeLocation}')`)
  const routeRequiredActions = getRouteRequiredActions(router, routeLocation)
  return canPerformActions(routeRequiredActions)
}

/**
 * Check the users permissions against a set of policy actions
 *
 * @param {keyof Actions[]} [policyActions=[]] - Array of policy actions to check
 *
 * @example                          - canPerformActions(['GetProjects', 'GetEvents'])
 * @returns {boolean}                - Does the user have sufficient permissions to perform all the given actions?
 */
function canPerformActions (policyActions = []) {
  if (!_.isArray(policyActions)) return false
  const rootStore = useRootStore()
  if (!rootStore.hasOrgs || rootStore.isOrgAdmin) return true
  const authStore = useAuthStore()

  return _.every(policyActions, (action) => authStore.evaluatedActions?.[action]?.ok ?? false)
}

function getInvalidActions (action) {
  const validKeys = _.keys(Actions)
  const invalidKeys = _.without([action], ...validKeys)
  return _.isEmpty(invalidKeys) ? null : invalidKeys
}

/** Create an object which has canDisplay method to check different kind of permissions
 *
 * @param {VueRouter} router - Vue router instance
 *
 * @returns A new instance
 */
export default function createPermissionChecker (router) {
  const devFeaturePermission = {
    localBE: {},
    nonLocalBE: {
      /* e.g. GetAppearance: true, */
    },
  }
  const devFeatureRoutePermission = {
    localBE: {},
    nonLocalBE: {
      /* e.g. orgSettingsAppearance: true, */
    },
  }

  const handleFeaturePermission = (action) => _.every([
    useDevStore().showDevThings,
    appMode.isLocalBE
      ? _.has(devFeaturePermission.localBE, action)
      : _.has(devFeaturePermission.nonLocalBE, action),
  ])
  const handleFeatureRoutePermission = (routeLocation) => _.every([
    useDevStore().showDevThings,
    appMode.isLocalBE
      ? _.has(devFeatureRoutePermission.localBE, routeLocation)
      : _.has(devFeatureRoutePermission.nonLocalBE, routeLocation),
  ])

  return {
    /** This method is called to determine which items should be disabled or hidden
     * based on existence of evaluated action in auth.evaluatedActions object.
     *
     * @param {keyof Actions}  action        - The permission key. (e.g. 'GetOrgs')
     * @param {string} [identifier]    - *optional* An entity id. (e.g. canonical, id, ref, etc.)     *
     * @returns {boolean}
     */
    canDisplay: (action, identifier) => {
      if (_.isArray(action)) {
        if (!appMode.isProduction) {
          console.error(canDisplayError, '\n Received:', { action, identifier }, '\n\n', new Error().stack)
          return
        }
        console.error(canDisplayError)
        return
      }

      // dev features
      if (handleFeaturePermission(action)) {
        return appMode.isLocalBE
          ? devFeaturePermission.localBE[action]
          : devFeaturePermission.nonLocalBE[action]
      }

      // fully implemented features
      return identifier
        ? checkUserPermissions(action, identifier)
        : checkUserPermissions(action)
    },

    /**
     * Check the users permissions against the given route
     *
     * @param {RouteLocation} routeLocation - Route location
     *
     * @returns {boolean}       - Does the user have sufficient permissions to access the given route?
     */
    canDisplayRoute: (routeLocation) => {
      // dev features
      if (handleFeatureRoutePermission(routeLocation)) {
        return devFeatureRoutePermission[routeLocation]
      }

      // fully implemented features
      return checkUserRoutePermissions(router, routeLocation)
    },

    /**
     * Whether an action has been evaluated and explicitly denied.
     *
     * Unlike `!canDisplay(action)`, this is false for never-evaluated actions, so
     * display-only gates (e.g. nav sections, whose pages stay protected by route
     * checks) can fail open instead of hiding before the action has been evaluated.
     *
     * @param {keyof Actions} action - The permission key. (e.g. 'GetMenuStacks')
     * @returns {boolean}
     */
    isDenied: (action) => isActionDenied(action),

    isEvaluated: (action) => isActionEvaluated(action),

    /**
     * Whether the current user owns the given project (BE-473 owner fallback). A gate on a
     * component write action ORs this in so a Project Creator can manage its own components.
     *
     * @param {string} orgCanonical
     * @param {string} projectCanonical
     * @returns {boolean}
     */
    ownsProject: (orgCanonical, projectCanonical) => ownsProject(orgCanonical, projectCanonical),

    /**
     * Get required actions for a given route
     *
     * @param {RouteLocation} routeLocation - Route location
     *
     * @returns {keyof Actions[]}         - The actions that a user must have to visit the given route
     */
    getRouteRequiredActions: (routeLocation) => getRouteRequiredActions(router, routeLocation),
  }
}
